import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { usePacote } from "../../core/common/PacoteContext/PacoteContext";
import {t} from "../../lang/index"

type DiferencialDTO = {
  id: number;
  tipo: string;
  descricaoCustomizada?: string;
  quantidade?: number;
};

type PacoteDTO = {
  id: number;
  titulo: string;
  descricao: string;
  precoBase: number;
  fotoUrls: string[];
  idiomas?: string[];
  diferenciais?: DiferencialDTO[];
};

const SearchBy = () => {
  const routes = all_routes;
  const BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { setPacotes } = usePacote();

  const [pacotes, setPacotesLocal] = useState<PacoteDTO[]>([]);
  const [selectedItems, setSelectedItems] = useState<boolean[]>([]);
  const [resumos, setResumos] = useState<Record<number, { media: number; total: number }>>({});

  // função para embaralhar array (Fisher–Yates shuffle)
  const shuffleArray = (array: PacoteDTO[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const fetchPacotes = async () => {
      try {
        const res = await fetch(`${BASE_URL}/pacotes/recentes`);
        if (!res.ok) throw new Error("Erro ao buscar pacotes recentes");
        const data: PacoteDTO[] = await res.json();

        // pega 10 do back, embaralha e escolhe 4
        const shuffled = shuffleArray(data).slice(0, 4);

        setPacotesLocal(shuffled);
        setSelectedItems(new Array(shuffled.length).fill(false));
      } catch (error) {
        console.error("Erro:", error);
      }
    };
    fetchPacotes();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchResumos = async () => {
      if (pacotes.length === 0) return;

      try {
        const responses = await Promise.all(
          pacotes.map((p) =>
            fetch(`${BASE_URL}/avaliacoes/pacote/${p.id}/resumo`).then((res) => res.json())
          )
        );

        const map: Record<number, { media: number; total: number }> = {};
        pacotes.forEach((p, idx) => {
          map[p.id!] = responses[idx];
        });

        setResumos(map);
      } catch (err) {
        console.error("Erro ao buscar resumos:", err);
      }
    };

    fetchResumos();
  }, [pacotes, BASE_URL]);

  const handleItemClick = (index: number) => {
    const updated = [...selectedItems];
    updated[index] = !updated[index];
    setSelectedItems(updated);
  };

  // Busca todos os pacotes e vai para grid
  const handleViewAll = async () => {
    try {
      const res = await fetch(`${BASE_URL}/pacotes/search`); // sem parâmetros = todos
      if (!res.ok) throw new Error("Erro ao buscar pacotes");

      const result: PacoteDTO[] = await res.json();
      setPacotes(result); // atualiza o contexto global
      navigate(all_routes.flightGrid); // navega para página de grid
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <section className="section destination-section blog-section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-10 text-center aos" data-aos="fade-up">
            <div className="section-header text-center">
              <h2 className="mb-2">
                <span className="text-primary text-decoration-underline">
                  {t("home.searchBy.titlePrefix")}
                </span>{" "}
                {t("home.searchBy.titleSuffix")}
              </h2>
              <p className="sub-title">{t("home.searchBy.subtitle")}</p>
            </div>
          </div>
        </div>

        {/* Grid de pacotes recentes */}
        <div className="row justify-content-start">
          {pacotes.length === 0 ? (
            <p>{t("home.searchBy.noPackages")}</p>
          ) : (
            pacotes.map((pacote, index) => {
              const fotoPrincipal: string =
                pacote.fotoUrls && pacote.fotoUrls.length > 0
                  ? pacote.fotoUrls[0]
                  : "assets/img/flight/flight-09.jpg";

              const finalUrl = fotoPrincipal.startsWith("http")
                ? fotoPrincipal
                : `${BASE_URL}${fotoPrincipal}`;

              return (
                <div className="col-10 col-md-6 col-lg-3 d-flex" key={pacote.id || index}>
                  <div
                    className="place-item common-grid-slider mb-4 flex-fill d-flex flex-column"
                    style={{ fontSize: "0.9rem", minHeight: "320px" }}
                  >
                    <div className="place-img">
                      <Link to={`/flight-details/${pacote.id}`}>
                        <img
                          src={finalUrl}
                          className="img-fluid"
                          alt={pacote.titulo || "Pacote"}
                          style={{ height: "180px", objectFit: "cover", borderRadius: "8px" }}
                        />
                      </Link>

                      <div className="fav-item" onClick={() => handleItemClick(index)}>
                        <div className="d-flex align-items-center">
                          <Link
                            to="#"
                            className={`fav-icon ${selectedItems[index] ? "selected" : ""}`}
                          >
                            <i className="isax isax-heart5"></i>
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div
                      className="place-content d-flex flex-column"
                      style={{ fontSize: "0.9rem", flexGrow: 1 }}
                    >
                      {/* Wrapper do topo (título + badge) */}
                      <div className="d-flex flex-column" style={{ gap: "0.25rem" }}>
                        <h6 className="text-truncate mb-0">
                          <Link to={`/flight-details/${pacote.id}`}>{pacote.titulo}</Link>
                        </h6>

                        {pacote.diferenciais && pacote.diferenciais.length > 0 && (
                          <span
                            className="badge fs-10 fw-medium d-flex align-items-center"
                            style={{
                              backgroundColor: "#cfe2ff",
                              color: "#0d6efd",
                              padding: "0.2rem 0.4rem",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              fontSize: "0.7rem",
                            }}
                          >
                            <i className="isax isax-star5 me-1"></i>
                            {t("home.searchBy.hasDiferencial")}
                          </span>
                        )}
                      </div>

                      {/* Preço sempre embaixo */}
                      <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-auto">
                        {/* Preço + avaliação */}
                        <div className="d-flex align-items-center">
                          <h6 className="text-primary m-0 me-2" style={{ fontSize: "0.9rem" }}>
                            $ {pacote.precoBase?.toFixed(2)}
                          </h6>

                          {resumos[pacote.id!] ? (
                            resumos[pacote.id!].total > 0 ? (
                              <div className="d-flex align-items-center">
                                <i className="isax isax-star1 text-warning me-1 fs-14" />
                                <span className="badge badge-warning badge-xs text-gray-9 fs-10 fw-medium me-1 p-1">
                                  <i className="isax isax-star" />
                                  {resumos[pacote.id!].media.toFixed(1)}
                                </span>
                                <small className="text-muted">
                                  ({resumos[pacote.id!].total})
                                </small>
                              </div>
                            ) : (
                              <small className="text-muted">{t("home.searchBy.noReviews")}</small>
                            )
                          ) : null}
                        </div>

                        {/* Idiomas no lado oposto */}
                        <div className="d-flex align-items-center flex-wrap">
                          {pacote.idiomas && pacote.idiomas.length > 0 && (
                            <>
                              {pacote.idiomas.slice(0, 2).map((idioma, i) => (
                                <span
                                  key={i}
                                  className="badge bg-outline-success fs-10 fw-medium me-1 mb-1"
                                >
                                  {idioma}
                                </span>
                              ))}
                              {pacote.idiomas.length > 2 && (
                                <span className="badge bg-outline-success fs-10 fw-medium mb-1">
                                  +{pacote.idiomas.length - 2} more
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Botão View All */}
        <div className="text-center view-all aos" data-aos="fade-up">
          <Link
            to="#"
            onClick={handleViewAll}
            className="btn btn-dark d-inline-flex align-items-center"
          >
            {t("home.searchBy.viewAll")}
            <i className="isax isax-arrow-right-3 ms-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SearchBy;